import React from 'react';
import { COUNTRY_CODES, getCountryLabel } from './countryCatalog';

function normalizeCountryName(value: string): string {
  return value.trim().toLowerCase();
}

const COUNTRY_TO_ISO2: Record<string, string> = (() => {
  const map: Record<string, string> = {};

  for (const code of COUNTRY_CODES) {
    const label = getCountryLabel(code, 'en');
    if (!label) continue;
    map[normalizeCountryName(label)] = code;
  }

  // Common aliases used in app data/constants
  map[normalizeCountryName('USA')] = 'US';
  map[normalizeCountryName('United States')] = 'US';
  map[normalizeCountryName('United Kingdom')] = 'GB';
  map[normalizeCountryName('England')] = 'GB';
  map[normalizeCountryName('Scotland')] = 'GB';
  map[normalizeCountryName('Wales')] = 'GB';
  map[normalizeCountryName('Northern Ireland')] = 'GB';
  map[normalizeCountryName("Cote d'Ivoire")] = 'CI';
  map[normalizeCountryName('Republic of the Congo')] = 'CG';
  map[normalizeCountryName('Democratic Republic of the Congo')] = 'CD';
  map[normalizeCountryName('Palestine')] = 'PS';
  map[normalizeCountryName('Russia')] = 'RU';
  map[normalizeCountryName('North Macedonia (formerly Macedonia)')] = 'MK';
  map[normalizeCountryName('Timor-Leste (East Timor)')] = 'TL';
  map[normalizeCountryName('United Arab Emirates (UAE)')] = 'AE';
  map[normalizeCountryName('Vatican City (Holy See)')] = 'VA';
  map[normalizeCountryName('Turkey')] = 'TR';
  map[normalizeCountryName('Czech Republic')] = 'CZ';
  map[normalizeCountryName('Myanmar (Burma)')] = 'MM';
  map[normalizeCountryName('Micronesia, Federated States of')] = 'FM';
  map[normalizeCountryName('Kosovo')] = 'XK';
  map[normalizeCountryName('South Korea')] = 'KR';
  map[normalizeCountryName('North Korea')] = 'KP';
  map[normalizeCountryName('Syria')] = 'SY';
  map[normalizeCountryName('Taiwan')] = 'TW';
  map[normalizeCountryName('Eswatini')] = 'SZ';
  map[normalizeCountryName('Cabo Verde')] = 'CV';
  map[normalizeCountryName('Sao Tome and Principe')] = 'ST';
  map[normalizeCountryName('Bosnia and Herzegovina')] = 'BA';

  return map;
})();

/** Resolve a country display name (or ISO code) to ISO 3166-1 alpha-2. */
export function countryToIso2(country: string): string | null {
  if (!country || typeof country !== 'string') return null;
  const trimmed = country.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  return COUNTRY_TO_ISO2[normalizeCountryName(trimmed)] || null;
}

/**
 * Flag emoji from regional-indicator code points.
 * Avoid in UI on Windows/Edge — Segoe UI Emoji often shows blank or letters.
 * Prefer {@link CountryFlag} (PNG via flagcdn) for display.
 */
function iso2ToFlagEmoji(iso2: string): string {
  const code = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🌍';
  const base = 127397;
  const chars = code.split('');
  return String.fromCodePoint(...chars.map(char => char.charCodeAt(0) + base));
}

export function countryToFlag(country: string): string {
  if (!country || typeof country !== 'string') return '🌍';
  const iso2 = countryToIso2(country);
  if (!iso2) return '🌍';
  return iso2ToFlagEmoji(iso2);
}

type CountryFlagProps = {
  country: string;
  className?: string;
  /** CSS width of the flag image (height scales ~3:4). Default 28. */
  width?: number;
  title?: string;
};

/**
 * Cross-browser country flag (PNG). Windows Edge does not reliably render
 * flag emojis; flagcdn images work in Chrome and Edge.
 */
export const CountryFlag: React.FC<CountryFlagProps> = ({
  country,
  className = '',
  width = 28,
  title,
}) => {
  const iso2 = countryToIso2(country);
  const height = Math.round((width * 3) / 4);

  if (!iso2) {
    return (
      <span className={className} title={title || country} aria-hidden>
        🌍
      </span>
    );
  }

  const code = iso2.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      width={width}
      height={height}
      alt=""
      title={title || country}
      className={`inline-block object-cover ${className}`.trim()}
      loading="lazy"
      decoding="async"
    />
  );
};
